import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import MatchFormActions from "./MatchFormActions";
import { useMatchDetailsContext } from "./matchDetails/MatchDetailsContext";
import { getAppSurfaceStyles } from "../theme/siteTheme";

interface MatchDetailsHeaderProps {
  onSave: () => Promise<void>;
  onPopulateSampleData: () => void;
  isAdmin: boolean;
}

const MatchDetailsHeader: React.FC<MatchDetailsHeaderProps> = ({
  onSave,
  onPopulateSampleData,
  isAdmin,
}) => {
  const { matchData } = useMatchDetailsContext();
  const theme = useTheme();
  const styles = getAppSurfaceStyles(theme);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            color: "text.primary",
            mb: 0,
            display: { xs: "none", sm: "block" },
          }}
        >
          Spiel-Details
        </Typography>
        {isAdmin && (
          <MatchFormActions
            onSave={onSave}
            onPopulateSampleData={onPopulateSampleData}
            isVertical={false}
          />
        )}
      </Box>

      <>
        {!matchData?.team_away?.name ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: 18, sm: 24, md: 28 },
                textAlign: "center",
              }}
            >
              {matchData?.team_home?.name || "Team"} hat Spielfrei
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 120,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  fontSize: { xs: 14, sm: 20, md: 22 },
                  letterSpacing: 1,
                }}
              >
                {matchData?.team_home?.name || "Heim"}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "primary.main",
                  fontWeight: 900,
                  fontSize: { xs: 26, sm: 32, md: 38 },
                  lineHeight: 1,
                }}
              >
                {matchData?.home_score ?? "-"}
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                mx: 2,
                fontSize: { xs: 24, sm: 30, md: 36 },
              }}
            >
              :
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 120,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "text.primary",
                  fontWeight: 700,
                  fontSize: { xs: 14, sm: 20, md: 22 },
                  letterSpacing: 1,
                }}
              >
                {matchData?.team_away?.name || "Gast"}{" "}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "text.primary",
                  fontWeight: 900,
                  fontSize: { xs: 26, sm: 32, md: 38 },
                  lineHeight: 1,
                }}
              >
                {matchData?.away_score ?? "-"}
              </Typography>
            </Box>
          </Box>
        )}
        <Typography
          variant="body1"
          sx={{
            mb: 1,
            textAlign: "center",
            color: "text.secondary",
            fontSize: { xs: 14, sm: 16, md: 18 },
            fontWeight: 500,
          }}
        >
          {matchData?.match_day?.date
            ? new Date(matchData.match_day.date).toLocaleDateString("de-DE")
            : "-"}
        </Typography>

        {matchData?.match_day?.date && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              textAlign: "center",
              color: "text.secondary",
              fontSize: { xs: 12, sm: 14, md: 16 },
              fontWeight: 400,
            }}
          >
            {matchData.team_home.spielbeginn} Uhr
          </Typography>
        )}

        {matchData?.team_home?.spielort && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
              color: styles.bodyText.color,
              fontSize: { xs: 11, sm: 13, md: 15 },
              fontWeight: 400,
              fontStyle: "italic",
            }}
          >
            Spielort: {matchData.team_home.spielort.name}
            {matchData.team_home.spielort.plzOrt &&
              `, ${matchData.team_home.spielort.plzOrt}`}
          </Typography>
        )}
      </>
    </>
  );
};

export default MatchDetailsHeader;
